/**
 * Estados honestos de tela (Task 10): carregando, erro, vazio e
 * "fonte ainda não publicada". São quatro coisas DIFERENTES e a Plataforma
 * nunca as confunde — um erro de rede não pode parecer "nenhum registro", e
 * fonte ausente não pode parecer zero.
 *
 * Redesign R9 (apresentação apenas): cada estado ganha um ícone (lucide)
 * correspondente, sempre acompanhado do texto — o ícone nunca é o único sinal
 * (régua §2). `data-status`, role (alert|status), aria-live e o texto do
 * título/detalhe são PRESERVADOS.
 */
import { createElement as h, type ReactElement, type ReactNode } from "react";
import { AlertCircle, Inbox, Loader2, type LucideIcon, TimerReset } from "lucide-react";

export type StatusKind = "loading" | "error" | "empty" | "awaiting_source";

export interface StatusPanelProps {
  kind: StatusKind;
  title: string;
  detail?: ReactNode;
}

/**
 * Borda-accent à ESQUERDA por estado (apresentação): erro = danger, fonte
 * ausente = warning; loading/empty ficam no cartão neutro. A cor vem de tokens
 * (brand/warning/danger), nunca de hex fixo, e o `role`/`data-status`/texto
 * seguem carregando o significado — a cor é reforço, não o único sinal.
 */
const ACCENT_BY_KIND: Record<StatusKind, string> = {
  loading: "",
  empty: "",
  error: "border-l-4 border-l-danger",
  awaiting_source: "border-l-4 border-l-warning"
};

const ICON_BY_KIND: Record<StatusKind, LucideIcon> = {
  loading: Loader2,
  error: AlertCircle,
  empty: Inbox,
  awaiting_source: TimerReset
};

const ICON_TONE_BY_KIND: Record<StatusKind, string> = {
  loading: "text-muted",
  error: "text-danger",
  empty: "text-muted",
  awaiting_source: "text-warning"
};

export function StatusPanel({ kind, title, detail }: StatusPanelProps): ReactElement {
  // Redesign R9 (apresentação apenas): cartão tokenizado ('rounded-panel border
  // border-border bg-surface p-4') + ícone por estado. As classes hook
  // `status-panel`/`status-panel--${kind}` seguem no markup (sem CSS legado
  // concorrente). `data-status`, role (alert|status), aria-live e o texto são
  // PRESERVADOS.
  const accent = ACCENT_BY_KIND[kind];
  const Icon = ICON_BY_KIND[kind];
  return h(
    "div",
    {
      className:
        `status-panel status-panel--${kind} flex gap-3 rounded-panel border border-border bg-surface p-4` +
        (accent ? ` ${accent}` : ""),
      "data-status": kind,
      role: kind === "error" ? "alert" : "status",
      "aria-live": kind === "loading" ? "polite" : undefined
    },
    h(Icon, {
      className: `mt-0.5 h-5 w-5 shrink-0 ${ICON_TONE_BY_KIND[kind]}` + (kind === "loading" ? " animate-spin" : ""),
      "aria-hidden": true,
      focusable: false
    }),
    h(
      "div",
      { className: "flex flex-col" },
      h("strong", { className: "status-panel__title font-semibold text-ink" }, title),
      detail ? h("p", { className: "status-panel__detail mt-1 text-sm text-body" }, detail) : null
    )
  );
}

/** Mensagem padrão de erro de carga, preservando a causa real para o operador. */
export function errorDetail(error: unknown): string {
  if (error instanceof Error) return error.message;
  return "erro desconhecido ao consultar a API da Plataforma";
}
